import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { ChevronDown } from "lucide-react";
import { ytEmbed } from "../../lib/constants";

export function FAQSection() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/api/faqs").then((r) => setItems(r.data || [])); }, []);
  if (items.length === 0) return null;
  return (
    <section className="px-6 lg:px-12 py-20" data-testid="home-faq">
      <div className="text-center reveal mb-10">
        <div className="eyebrow">Frequently Asked</div>
        <h2 className="font-display text-4xl lg:text-5xl text-maroon mt-1">Your questions, answered.</h2>
      </div>
      <div className="max-w-3xl mx-auto space-y-2">
        {items.map((f) => (
          <div key={f.id} className="bg-white border border-line">
            <button onClick={() => setOpen(open === f.id ? null : f.id)} className="w-full text-left p-4 flex items-center justify-between" data-testid={`faq-q-${f.id}`}>
              <span className="font-medium">{f.question}</span>
              <ChevronDown size={16} className={`transition-transform ${open === f.id ? "rotate-180" : ""}`}/>
            </button>
            {open === f.id && <div className="px-4 pb-4 text-sm text-ash">{f.answer}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/api/testimonials").then((r) => setItems(r.data || [])); }, []);
  if (items.length === 0) return null;
  return (
    <section className="px-6 lg:px-12 py-20 bg-gradient-to-b from-pearl to-white">
      <div className="text-center mb-10 reveal">
        <div className="eyebrow">Words from our family</div>
        <h2 className="font-display text-4xl lg:text-5xl text-maroon mt-1">What customers say</h2>
      </div>
      <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {items.map((t, i) => (
          <div key={t.id} className="bg-white border border-line p-6 hover-lift reveal" style={{ transitionDelay: `${i*80}ms` }} data-testid={`testimonial-${t.id}`}>
            {t.image_url && <img src={t.image_url} alt="" className="w-14 h-14 rounded-full object-cover mb-3"/>}
            <div className="text-gold text-lg">{"★".repeat(t.rating || 5)}</div>
            <p className="italic text-ink mt-2">"{t.quote}"</p>
            <div className="text-xs text-ash mt-3">— {t.customer_name}{t.location && `, ${t.location}`}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function BannerSlider() {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => { api.get("/api/banners?kind=seasonal").then((r) => setItems(r.data || [])); }, []);
  useEffect(() => { if (items.length > 1) { const i = setInterval(() => setIdx(x => (x+1) % items.length), 5000); return () => clearInterval(i); } }, [items]);
  if (items.length === 0) return null;
  const b = items[idx];
  return (
    <section className="px-6 lg:px-12 py-12" data-testid="seasonal-slider">
      <a href={b.cta_link || "#"} className="relative block aspect-[16/5] overflow-hidden group">
        <img src={b.image_url} alt={b.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className="absolute inset-0 bg-gradient-to-r from-maroon-dark/80 via-maroon/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-center px-10 text-pearl">
          <div className="eyebrow text-gold-light">Seasonal</div>
          <h3 className="font-display text-4xl mt-2">{b.title}</h3>
          {b.subtitle && <p className="text-pearl/80 mt-2 max-w-md">{b.subtitle}</p>}
          {b.cta_text && <span className="btn-gold mt-4 inline-flex w-fit">{b.cta_text}</span>}
        </div>
      </a>
      {items.length > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          {items.map((_, i) => <button key={i} onClick={() => setIdx(i)} className={`w-2 h-2 rounded-full ${i === idx ? "bg-maroon" : "bg-line"}`}/>)}
        </div>
      )}
    </section>
  );
}

export function ShowroomVideo() {
  const [url, setUrl] = useState("");
  useEffect(() => { api.get("/api/site-settings").then((r) => setUrl(r.data?.showroom_video_url || "")); }, []);
  const embed = ytEmbed(url);
  if (!embed) return null;
  return (
    <section className="px-6 lg:px-12 py-20">
      <div className="text-center mb-8 reveal">
        <div className="eyebrow">Step inside</div>
        <h2 className="font-display text-4xl lg:text-5xl text-maroon mt-1">A walk through our showroom</h2>
      </div>
      <div className="max-w-4xl mx-auto aspect-video">
        <iframe src={embed} title="Showroom" allowFullScreen className="w-full h-full" data-testid="showroom-video"/>
      </div>
    </section>
  );
}
